import type { EstimateDocRichText } from '@notifi/shared-types/estimate-doc-template';

/**
 * Serialize the authored-prose blocks' TipTap/ProseMirror document JSON
 * (`authText` / `ackText` / `richText`) to the bounded editor HTML that
 * `RichTextPdf` consumes — so the templated PDF preserves the SAME headings,
 * lists and text marks the hosted document renders through `DocRichText`,
 * instead of flattening everything to one plain paragraph.
 *
 * Coverage mirrors `DocRichText` exactly (paragraph, heading, bullet/ordered
 * lists, and bold/italic/underline/strike marks); an unknown node falls through
 * to its children so no authored copy is silently dropped. Text is HTML-escaped
 * before wrapping, and `RichTextPdf` sanitizes again on the way in.
 */

interface ProseNode {
  type?: string;
  text?: string;
  content?: ProseNode[];
  marks?: { type?: string }[];
  attrs?: { level?: number };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function wrapMarks(text: string, marks: ProseNode['marks']): string {
  if (!marks || marks.length === 0) return text;
  return marks.reduce((html, mark) => {
    switch (mark.type) {
      case 'bold':
      case 'strong':
        return `<strong>${html}</strong>`;
      case 'italic':
      case 'em':
        return `<em>${html}</em>`;
      case 'underline':
        return `<u>${html}</u>`;
      case 'strike':
        return `<s>${html}</s>`;
      default:
        return html;
    }
  }, text);
}

function nodeToHtml(node: ProseNode): string {
  switch (node.type) {
    case 'text':
      return wrapMarks(escapeHtml(node.text ?? ''), node.marks);
    case 'hardBreak':
      return '<br />';
    case 'paragraph':
      return `<p>${childrenToHtml(node.content)}</p>`;
    case 'heading': {
      const level = Math.min(Math.max(node.attrs?.level ?? 2, 1), 6);
      return `<h${level}>${childrenToHtml(node.content)}</h${level}>`;
    }
    case 'bulletList':
      return `<ul>${childrenToHtml(node.content)}</ul>`;
    case 'orderedList':
      return `<ol>${childrenToHtml(node.content)}</ol>`;
    case 'listItem':
      return `<li>${childrenToHtml(node.content)}</li>`;
    default:
      return node.content ? childrenToHtml(node.content) : escapeHtml(node.text ?? '');
  }
}

function childrenToHtml(nodes: ProseNode[] | undefined): string {
  return (nodes ?? []).map(nodeToHtml).join('');
}

/** TipTap JSON (or a plain string) → editor HTML for `RichTextPdf`. */
export function proseToHtml(content: EstimateDocRichText | string | null | undefined): string {
  if (!content) return '';
  if (typeof content === 'string') {
    const trimmed = content.trim();
    return trimmed ? `<p>${escapeHtml(trimmed)}</p>` : '';
  }
  return childrenToHtml((content as { content?: ProseNode[] }).content);
}
